import React, { useEffect, useState } from "react";
import "./newUser.css";
import { Collapse, ConfigProvider, Switch, Tooltip } from "antd";
import { useSelector } from "react-redux";
import {
    permissionCellStyle,
    permissionLabelStyle,
    permissionSwitchTheme,
    permissionThemeVars,
} from "../../theme/globalStyle/globalStyle";
import {
    calcularSelecionarTodosGeralPorEscopo,
    canGrantPermission,
    canUsePermissionDevMode,
    groupHasGrantablePermission,
} from "../../util/permissions/permissionScope";

const { Panel } = Collapse;

/** Chaves de grupo exibidas neste componente — usado para “Selecionar todos” geral coerente com os switches. */
export const PERMISSION_MASTER_GROUP_KEYS = [
    "salesDashboard",
    "client",
    "user",
    "driver",
    "shipper",
    "vehicle",
    "freight",
    "freightPricing",
    "freightDriver",
    "freightFreight",
    "freightCannotRelease",
    "freightCancel",
    "freightStoppingPointLatLong",
    "product",
    "mdfe",
    "import",
    "payment",
    "freightControl",
    "freightControlDocuments",
    "freightControlBoleto",
    "tableIcms",
    "ocurrence",
    "revenue",
    "tablePrice",
    "perfil",
    "generalReport",
    "paymentCondition",
    "accompaniment",
    "simulatorFreight",
    "lances",
    "filasTransportadoras",
    "budget",
];

/**
 * Retorna true somente se, em cada grupo da UI, `selecionarTodos` estiver true
 * (ou seja, todos os flags do grupo estão alinhados com “marcar tudo” daquele grupo).
 * Ignora o flag persistido no banco quando ele não reflete o estado real.
 */
export function calcularSelecionarTodosGeralCoerente(
    permissoes,
    allowedPermissions,
) {
    if (!permissoes || typeof permissoes !== "object") return false;
    // null = modo sem escopo (matriz completa / switch devMode ligado)
    if (allowedPermissions === null) {
        return calcularSelecionarTodosGeralPorEscopo(permissoes, null);
    }
    if (allowedPermissions) {
        return calcularSelecionarTodosGeralPorEscopo(
            permissoes,
            allowedPermissions,
        );
    }
    return PERMISSION_MASTER_GROUP_KEYS.every((key) => {
        const g = permissoes[key];
        return Boolean(g && g.selecionarTodos === true);
    });
}

export default function Permission({
    view,
    permissoes,
    allowedPermissions,
    handleChange,
    handleSelecionarTodosGeral,
    /** Estado temporário do switch (controlado pelo pai quando precisa sincronizar clamp/save). */
    isDevModeEnabled: isDevModeEnabledProp,
    onDevModeEnabledChange,
}) {
    const operator = useSelector((state) => state.user);
    const canShowDevModeSwitch = canUsePermissionDevMode(operator);

    const [isDevModeEnabledLocal, setIsDevModeEnabledLocal] = useState(false);
    const isControlled = typeof onDevModeEnabledChange === "function";
    const isDevModeEnabled = isControlled
        ? Boolean(isDevModeEnabledProp)
        : isDevModeEnabledLocal;

    useEffect(() => {
        if (!canShowDevModeSwitch) {
            if (isControlled) {
                if (isDevModeEnabledProp) onDevModeEnabledChange(false);
            } else if (isDevModeEnabledLocal) {
                setIsDevModeEnabledLocal(false);
            }
        }
    }, [
        canShowDevModeSwitch,
        isControlled,
        isDevModeEnabledProp,
        isDevModeEnabledLocal,
        onDevModeEnabledChange,
    ]);

    /** Única decisão: adaptação ativa vs matriz completa. */
    const isUnscoped =
        canShowDevModeSwitch && isDevModeEnabled === true;
    const effectiveAllowedPermissions = isUnscoped
        ? null
        : allowedPermissions;

    const setDevModeEnabled = (next) => {
        if (!canShowDevModeSwitch) return;
        if (isControlled) {
            onDevModeEnabledChange(Boolean(next));
        } else {
            setIsDevModeEnabledLocal(Boolean(next));
        }
    };

    const groupsCreate = [
        { key: "salesDashboard", label: "Dashboard Vendas" },
        { key: "simulatorFreight", label: "Simulador de Frete" },
        { key: "lances", label: "Lances" },
        { key: "filasTransportadoras", label: "Filas de Transportadoras" },
        { key: "client", label: "Cliente" },
        { key: "user", label: "Usuário" },
        { key: "driver", label: "Motorista" },
        { key: "shipper", label: "Embarcador" },
        { key: "vehicle", label: "Veículo" },
        { key: "freight", label: "Frete" },
        { key: "product", label: "Produto" },
        { key: "budget", label: "Orçamento" },
    ];

    const groupsFreightSubPermissions = [
        { key: "freightPricing", label: "Precificação" },
        { key: "freightDriver", label: "Motorista" },
        { key: "freightFreight", label: "Frete" },
        { key: "freightCannotRelease", label: "Liberar frete" },
        { key: "freightCancel", label: "Cancelar frete" },
        // { key: "freightViewEditDriver", label: "Motorista no frete (visualizar/editar)" },
    ];

    const groupsFreightStoppingPoint = [
        { key: "freightStoppingPointLatLong", label: "Latitude e Longitude" },
    ];

    const groupsFiscal = [
        { key: "mdfe", label: "MDF-E" },
        { key: "import", label: "Importação" },
    ];

    const groupsPayment = [
        { key: "payment", label: "Pagamento" },
        { key: "freightControl", label: "Faturas" },
    ];

    const groupsFreightInvoiceControlSubPermissions = [
        { key: "freightControlDocuments", label: "Documentos da fatura" },
        { key: "freightControlBoleto", label: "Boleto" },
    ];

    const groupsParameters = [
        { key: "tableIcms", label: "Tabela ICMS" },
        { key: "ocurrence", label: "Ocorrências" },
        { key: "tablePrice", label: "Tabela de Preço" },
        { key: "perfil", label: "Perfil" },
        { key: "revenue", label: "Faturamento" },
        { key: "generalReport", label: "Relatório Geral" },
        { key: "paymentCondition", label: "Condição de Pagamento" },
    ];

    const groupsAcomp = [
        { key: "accompaniment", label: "Acompanhamento" },
    ]

    const hasPermissionScope = Boolean(effectiveAllowedPermissions);
    const canRenderGroup = (groupKey) =>
        !hasPermissionScope ||
        groupHasGrantablePermission(effectiveAllowedPermissions, groupKey);
    const filterGrantableGroups = (groups) => groups.filter((g) => canRenderGroup(g.key));

    const groupsCreateVisible = filterGrantableGroups(groupsCreate);
    const groupsFreightSubPermissionsVisible = filterGrantableGroups(
        groupsFreightSubPermissions,
    );
    const groupsFreightStoppingPointVisible = filterGrantableGroups(
        groupsFreightStoppingPoint,
    );
    const groupsFiscalVisible = filterGrantableGroups(groupsFiscal);
    const groupsPaymentVisible = filterGrantableGroups(groupsPayment);
    const groupsFreightInvoiceControlSubPermissionsVisible =
        filterGrantableGroups(groupsFreightInvoiceControlSubPermissions);
    const groupsParametersVisible = filterGrantableGroups(groupsParameters);
    const groupsAcompVisible = filterGrantableGroups(groupsAcomp);
    const hasAnyGrantablePermission = [
        groupsCreateVisible,
        groupsFreightSubPermissionsVisible,
        groupsFreightStoppingPointVisible,
        groupsFiscalVisible,
        groupsPaymentVisible,
        groupsFreightInvoiceControlSubPermissionsVisible,
        groupsParametersVisible,
        groupsAcompVisible,
    ].some((groups) => groups.length > 0);

    const renderSwitch = (groupKey, permissionField) => {
        if (
            hasPermissionScope &&
            !canGrantPermission(effectiveAllowedPermissions, groupKey, permissionField)
        ) {
            return <td style={permissionCellStyle} />;
        }

        const checked = !!permissoes?.[groupKey]?.[permissionField];

        return (
            <td style={permissionCellStyle}>
                <Switch
                    disabled={view}
                    checked={checked}
                    onChange={() => handleChange(groupKey, permissionField)}
                    className="permission-switch"
                />
            </td>
        );
    };

    const renderCell = (groupKey, fieldKey) => {
        if (fieldKey === "null") {
            return <td style={permissionCellStyle} />;
        }

        if (fieldKey === "single") {
            return renderSwitch(groupKey, "isView");
        }

        return renderSwitch(groupKey, fieldKey);
    };

    // Escopo efetivo (null em devMode) — só para o checked do master.
    // Ligar/desligar o switch devMode NÃO chama handleSelecionarTodosGeral.
    const selecionarTodosGeralCoerente =
        calcularSelecionarTodosGeralCoerente(
            permissoes,
            effectiveAllowedPermissions,
        );

    const handleDevModeSwitchChange = (next, event) => {
        event?.stopPropagation?.();
        event?.preventDefault?.();
        setDevModeEnabled(next);
    };

    return (
        <ConfigProvider theme={permissionSwitchTheme}>
            <div className="permission-root" style={{ width: "100%", overflowX: "auto", ...permissionThemeVars }}>
                <div className="permission-select-all">
                    <div style={permissionLabelStyle}>
                        <Switch
                            disabled={view || !hasAnyGrantablePermission}
                            checked={selecionarTodosGeralCoerente}
                            onChange={handleSelecionarTodosGeral}
                            className="permission-switch"
                        />
                        <span>Selecionar Todos</span>
                    </div>
                    {canShowDevModeSwitch ? (
                        <Tooltip
                            title={
                                isDevModeEnabled
                                    ? "Ligado: visualização completa para desenvolvimento."
                                    : "Desligado: permissões adaptadas ao acesso do operador."
                            }
                        >
                            <div
                                style={{
                                    ...permissionLabelStyle,
                                    marginLeft: "auto",
                                }}
                                data-testid="permission-devmode-switch"
                                onClick={(event) => event.stopPropagation()}
                            >
                                <Switch
                                    disabled={view}
                                    checked={isDevModeEnabled}
                                    onChange={handleDevModeSwitchChange}
                                    className="permission-switch"
                                />
                                <span>devMode</span>
                            </div>
                        </Tooltip>
                    ) : null}
                </div>

                {groupsCreateVisible.length > 0 && (
                    <Collapse accordion className="collapse-full" >
                        <Panel header={"Cadastros"} key={"permissoes"}>
                            <table className="permission-table" style={{ width: "100%", borderCollapse: "collapse" }}>
                                <thead>
                                    <tr style={{ background: "#f5f5f5" }}>
                                        <th style={{ textAlign: "left", padding: "8px 12px" }}></th>
                                        <th style={{ textAlign: "center", padding: "8px 12px" }}>Selecionar Todos</th>
                                        <th style={{ textAlign: "center", padding: "8px 12px" }}>Visualizar</th>
                                        <th style={{ textAlign: "center", padding: "8px 12px" }}>Cadastrar</th>
                                        <th style={{ textAlign: "center", padding: "8px 12px" }}>Editar</th>
                                        <th style={{ textAlign: "center", padding: "8px 12px" }}>Excluir</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {groupsCreateVisible.map((g) => {
                                        if (g.key === "salesDashboard" || g.key === "simulatorFreight" || g.key === "lances" || g.key === "freightCarrierApplications" || g.key === "filasTransportadoras") {
                                            return (
                                                <tr key={g.key} style={{ borderBottom: "1px solid #eee" }}>
                                                    <td style={{ padding: "8px 12px", width: "280px" }}>{g.label}</td>
                                                    {renderCell(g.key, "null")}
                                                    {renderCell(g.key, "single")}
                                                </tr>
                                            );
                                        }
                                        return (
                                            <tr key={g.key} style={{ borderBottom: "1px solid #eee" }}>
                                                <td style={{ padding: "8px 12px", width: "280px" }}>{g.label}</td>
                                                {renderCell(g.key, "selecionarTodos")}
                                                {renderCell(g.key, "isView")}
                                                {renderCell(g.key, "isCreate")}
                                                {renderCell(g.key, "isEdit")}
                                                {renderCell(g.key, "isDelete")}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                            {groupsFreightSubPermissionsVisible.length > 0 && (
                                <Collapse className="collapse-full" style={{ marginTop: 12 }}>
                                    <Panel header={"Frete Sub Permissões"} key={"cadastros-frete-sub-permissoes"}>
                                        <table className="permission-table" style={{ width: "100%", borderCollapse: "collapse" }}>
                                            <thead>
                                                <tr style={{ background: "#f5f5f5" }}>
                                                    <th style={{ textAlign: "left", padding: "8px 12px" }}></th>
                                                    {/* <th style={{ textAlign: "center", padding: "8px 12px" }}>Selecionar Todos</th> */}
                                                    <th style={{ textAlign: "center", padding: "8px 12px" }}>Visualizar</th>
                                                    {/* <th style={{ textAlign: "center", padding: "8px 12px" }}>Cadastrar</th> */}
                                                    <th style={{ textAlign: "center", padding: "8px 12px" }}>Editar</th>
                                                    {/* <th style={{ textAlign: "center", padding: "8px 12px" }}>Excluir</th> */}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {groupsFreightSubPermissionsVisible.map((g) => {
                                                    if (g.key === "freightCannotRelease") {
                                                        return (
                                                            <tr key={g.key} style={{ borderBottom: "1px solid #eee" }}>
                                                                <td style={{ padding: "8px 12px", width: "280px" }}>{g.label}</td>
                                                                {renderCell(g.key, "single")}
                                                                <td style={permissionCellStyle} />
                                                            </tr>
                                                        );
                                                    }
                                                    if (g.key === "freightCancel") {
                                                        return (
                                                            <tr key={g.key} style={{ borderBottom: "1px solid #eee" }}>
                                                                <td style={{ padding: "8px 12px", width: "280px" }}>{g.label}</td>
                                                                {renderCell(g.key, "single")}
                                                                <td style={permissionCellStyle} />
                                                            </tr>
                                                        );
                                                    }
                                                    return (
                                                        <tr key={g.key} style={{ borderBottom: "1px solid #eee" }}>
                                                            <td style={{ padding: "8px 12px", width: "280px" }}>{g.label}</td>
                                                            {/* {renderCell(g.key, "selecionarTodos")} */}
                                                            {renderCell(g.key, "isView")}
                                                            {/* {renderCell(g.key, "isCreate")} */}
                                                            {renderCell(g.key, "isEdit")}
                                                            {/* {renderCell(g.key, "isDelete")} */}
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>

                                        {groupsFreightStoppingPointVisible.length > 0 && (
                                            <Collapse className="collapse-full" style={{ marginTop: 12 }}>
                                                <Panel header={"Pontos de Parada"} key={"cadastros-frete-pontos-de-parada"}>
                                                    <table className="permission-table" style={{ width: "100%", borderCollapse: "collapse" }}>
                                                        <thead>
                                                            <tr style={{ background: "#f5f5f5" }}>
                                                                <th style={{ textAlign: "left", padding: "8px 12px" }}></th>
                                                                {/* <th style={{ textAlign: "center", padding: "8px 12px" }}>Selecionar Todos</th> */}
                                                                <th style={{ textAlign: "center", padding: "8px 12px" }}>Visualizar</th>
                                                                {/* <th style={{ textAlign: "center", padding: "8px 12px" }}>Cadastrar</th> */}
                                                                {/* <th style={{ textAlign: "center", padding: "8px 12px" }}>Editar</th> */}
                                                                {/* <th style={{ textAlign: "center", padding: "8px 12px" }}>Excluir</th> */}
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {groupsFreightStoppingPointVisible.map((g) => (
                                                                <tr key={g.key} style={{ borderBottom: "1px solid #eee" }}>
                                                                    <td style={{ padding: "8px 12px", width: "280px" }}>{g.label}</td>
                                                                    {/* {renderCell(g.key, "selecionarTodos")} */}
                                                                    {renderCell(g.key, "isView")}
                                                                    {/* {renderCell(g.key, "isCreate")} */}
                                                                    {/* {renderCell(g.key, "isEdit")} */}
                                                                    {/* {renderCell(g.key, "isDelete")} */}
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </Panel>
                                            </Collapse>
                                        )}
                                    </Panel>
                                </Collapse>
                            )}
                        </Panel>
                    </Collapse>
                )}

                {groupsFiscalVisible.length > 0 && (
                    <Collapse accordion className="collapse-full" >
                        <Panel header={"Fiscal"} key={"permissoes-repetido"}>
                            <table className="permission-table" style={{ width: "100%", borderCollapse: "collapse" }}>
                                <thead>
                                    <tr style={{ background: "#f5f5f5" }}>
                                        <th style={{ textAlign: "left", padding: "8px 12px" }}></th>
                                        <th style={{ textAlign: "center", padding: "8px 12px" }}>Selecionar Todos</th>
                                        <th style={{ textAlign: "center", padding: "8px 12px" }}>Visualizar</th>
                                        <th style={{ textAlign: "center", padding: "8px 12px" }}>Cadastrar</th>
                                        <th style={{ textAlign: "center", padding: "8px 12px" }}>Editar</th>
                                        <th style={{ textAlign: "center", padding: "8px 12px" }}>Excluir</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {groupsFiscalVisible.map((g) => (
                                        <tr key={g.key} style={{ borderBottom: "1px solid #eee" }}>
                                            <td style={{ padding: "8px 12px", width: "280px" }}>{g.label}</td>
                                            {renderCell(g.key, "selecionarTodos")}
                                            {renderCell(g.key, "isView")}
                                            {renderCell(g.key, "isCreate")}
                                            {renderCell(g.key, "isEdit")}
                                            {renderCell(g.key, "isDelete")}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </Panel>
                    </Collapse>
                )}

                {groupsPaymentVisible.length > 0 && (
                    <Collapse accordion className="collapse-full" >
                        <Panel header={"Financeiro"} key={"permissoes-financeiro"}>
                            <table className="permission-table" style={{ width: "100%", borderCollapse: "collapse" }}>
                                <thead>
                                    <tr style={{ background: "#f5f5f5" }}>
                                        <th style={{ textAlign: "left", padding: "8px 12px" }}></th>
                                        <th style={{ textAlign: "center", padding: "8px 12px" }}>Selecionar Todos</th>
                                        <th style={{ textAlign: "center", padding: "8px 12px" }}>Visualizar</th>
                                        <th style={{ textAlign: "center", padding: "8px 12px" }}>Cadastrar</th>
                                        <th style={{ textAlign: "center", padding: "8px 12px" }}>Editar</th>
                                        <th style={{ textAlign: "center", padding: "8px 12px" }}>Excluir</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {groupsPaymentVisible.map((g) => {
                                        if (g.key === "freightControl") {
                                            return (
                                                <tr key={g.key} style={{ borderBottom: "1px solid #eee" }}>
                                                    <td style={{ padding: "8px 12px", width: "280px" }}>{g.label}</td>
                                                    {renderCell(g.key, "selecionarTodos")}
                                                    {renderCell(g.key, "isView")}
                                                    {renderCell(g.key, "null")}
                                                    {renderCell(g.key, "isEdit")}
                                                    {renderCell(g.key, "null")}
                                                </tr>
                                            );
                                        }

                                        return (
                                            <tr key={g.key} style={{ borderBottom: "1px solid #eee" }}>
                                                <td style={{ padding: "8px 12px", width: "280px" }}>{g.label}</td>
                                                {renderCell(g.key, "selecionarTodos")}
                                                {renderCell(g.key, "isView")}
                                                {renderCell(g.key, "isCreate")}
                                                {renderCell(g.key, "isEdit")}
                                                {renderCell(g.key, "isDelete")}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                            {groupsFreightInvoiceControlSubPermissionsVisible.length > 0 && (
                                <Collapse className="collapse-full" style={{ marginTop: 12 }}>
                                    <Panel
                                        header={"Painel de Faturas Sub Permissões"}
                                        key={"financeiro-freight-invoice-control-sub"}
                                    >
                                        <table className="permission-table" style={{ width: "100%", borderCollapse: "collapse" }}>
                                            <thead>
                                                <tr style={{ background: "#f5f5f5" }}>
                                                    <th style={{ textAlign: "left", padding: "8px 12px" }}></th>
                                                    <th style={{ textAlign: "center", padding: "8px 12px" }}>Visualizar</th>
                                                    <th style={{ textAlign: "center", padding: "8px 12px" }}>Editar</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {groupsFreightInvoiceControlSubPermissionsVisible.map((g) => {
                                                    if (g.key === "freightControlDocuments") {
                                                        return (
                                                            <tr key={g.key} style={{ borderBottom: "1px solid #eee" }}>
                                                                <td style={{ padding: "8px 12px", width: "280px" }}>{g.label}</td>
                                                                {renderCell(g.key, "single")}
                                                                <td style={permissionCellStyle} />
                                                            </tr>
                                                        );
                                                    }

                                                    return (
                                                        <tr key={g.key} style={{ borderBottom: "1px solid #eee" }}>
                                                            <td style={{ padding: "8px 12px", width: "280px" }}>{g.label}</td>
                                                            {renderCell(g.key, "isView")}
                                                            {renderCell(g.key, "isEdit")}
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </Panel>
                                </Collapse>
                            )}
                        </Panel>
                    </Collapse>
                )}

                {groupsParametersVisible.length > 0 && (
                    <Collapse accordion className="collapse-full" >
                        <Panel header={"Parâmetros"} key={"permissoes-parametro"}>
                            <table className="permission-table" style={{ width: "100%", borderCollapse: "collapse" }}>
                                <thead>
                                    <tr style={{ background: "#f5f5f5" }}>
                                        <th style={{ textAlign: "left", padding: "8px 12px" }}></th>
                                        <th style={{ textAlign: "center", padding: "8px 12px" }}>Selecionar Todos</th>
                                        <th style={{ textAlign: "center", padding: "8px 12px" }}>Visualizar</th>
                                        <th style={{ textAlign: "center", padding: "8px 12px" }}>Cadastrar</th>
                                        <th style={{ textAlign: "center", padding: "8px 12px" }}>Editar</th>
                                        <th style={{ textAlign: "center", padding: "8px 12px" }}>Excluir</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {groupsParametersVisible.map((g) => {
                                        if (g.key === "revenue" || g.key === "generalReport") {
                                            return (
                                                <tr key={g.key} style={{ borderBottom: "1px solid #eee" }}>
                                                    <td style={{ padding: "8px 12px", width: "280px" }}>{g.label}</td>
                                                    {renderCell(g.key, "null")}
                                                    {renderCell(g.key, "single")}
                                                </tr>
                                            );
                                        }
                                        return (
                                            <tr key={g.key} style={{ borderBottom: "1px solid #eee" }}>
                                                <td style={{ padding: "8px 12px", width: "280px" }}>{g.label}</td>
                                                {renderCell(g.key, "selecionarTodos")}
                                                {renderCell(g.key, "isView")}
                                                {renderCell(g.key, "isCreate")}
                                                {renderCell(g.key, "isEdit")}
                                                {renderCell(g.key, "isDelete")}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </Panel>
                    </Collapse>
                )}

                {groupsAcompVisible.length > 0 && (
                    <Collapse accordion className="collapse-full" >
                        <Panel header={"Acompanhamento"} key={"permissoes-acompanhamento"}>
                            <table className="permission-table" style={{ width: "100%", borderCollapse: "collapse" }}>
                                <thead>
                                    <tr style={{ background: "#f5f5f5" }}>
                                        <th style={{ textAlign: "left", padding: "8px 12px" }}></th>
                                        <th style={{ textAlign: "center", padding: "8px 12px" }}>Visualizar</th>
                                        {/* <th style={{ textAlign: "center", padding: "8px 12px" }}>Cadastrar</th>
                                <th style={{ textAlign: "center", padding: "8px 12px" }}>Editar</th>
                                <th style={{ textAlign: "center", padding: "8px 12px" }}>Excluir</th> */}
                                    </tr>
                                </thead>
                                <tbody>
                                    {groupsAcompVisible.map((g) => (
                                        <tr key={g.key} style={{ borderBottom: "1px solid #eee" }}>
                                            <td style={{ padding: "8px 12px", width: "280px" }}>{g.label}</td>
                                            {renderCell(g.key, "single")}
                                            {/* {renderCell(g.key, "isCreate")}
                                    {renderCell(g.key, "isEdit")}
                                    {renderCell(g.key, "isDelete")} */}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </Panel>
                    </Collapse>
                )}
            </div>
        </ConfigProvider>
    );

}
